
import React, { useState } from 'react';
import { db } from '@/services/mockDb';
import { User, Role } from '@/types';
import { v4 as uuidv4 } from 'uuid';
import { Shield, Trash2, UserPlus, X, Edit2 } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';

const Clients: React.FC = () => {
  const [users, setUsers] = useState<User[]>(db.getSystemUsers());
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<Partial<User>>({});
  const [isSaving, setIsSaving] = useState(false);

  const { user: authUser, isAdmin } = useAuth();
  
  // Security check: only authenticated admins can see this
  if (!authUser || !isAdmin) {
     return (
       <div className="text-center py-20">
         <Shield size={48} className="mx-auto text-gray-400 mb-4" />
         <h2 className="text-xl font-bold text-gray-700">Acesso Negado</h2>
         <p className="text-gray-500">Apenas administradores podem gerenciar usuários.</p>
       </div>
     );
  }

  const handleOpenNew = () => {
    setEditingUser({ role: 'staff' });
    setShowModal(true);
  };

  const handleOpenEdit = (user: User) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDelete = async (userId: string) => {
    if (userId === authUser?.uid) {
      alert("Você não pode excluir a si mesmo!");
      return;
    }
    if (confirm('Tem certeza que deseja remover o acesso deste usuário?')) {
      await db.removeSystemUser(userId);
      setUsers(db.getSystemUsers());
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      if (editingUser.uid) {
        // Update
        await db.updateSystemUser(editingUser as User);
      } else {
        // Create
        const newUser: User = {
          uid: uuidv4(),
          displayName: editingUser.displayName!,
          email: editingUser.email!,
          role: editingUser.role || 'staff',
          createdAt: new Date(),
          lastLogin: new Date()
        };
        await db.addSystemUser(newUser);
      }
      setUsers(db.getSystemUsers());
      setShowModal(false);
    } catch (err) {
      alert("Erro ao salvar usuário.");
    } finally {
      setIsSaving(false);
    }
  };

  const getRoleLabel = (role: Role) => {
    switch (role) {
      case 'admin': return <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-bold uppercase">Administrador</span>;
      case 'manager': return <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold uppercase">Gerente</span>;
      case 'staff': return <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold uppercase">Equipe (Staff)</span>;
      case 'guide': return <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs font-bold uppercase">Guia</span>;
      case 'leader': return <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-bold uppercase">Líder (Cliente)</span>;
      default: return role;
    }
  };

  return (
    <div>
       <div className="flex justify-between items-center mb-8">
         <div>
           <h1 className="text-2xl font-bold text-gray-800">Controle de Acesso</h1>
           <p className="text-gray-500">Gerencie quem pode acessar o sistema e suas permissões</p>
         </div>
         <button 
           onClick={handleOpenNew}
           className="bg-[#FF6B35] text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-[#E85A2C]"
         >
           <UserPlus size={18} /> Novo Usuário
         </button>
       </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
             <table className="w-full text-left min-w-[600px]">
               <thead className="bg-gray-50 border-b border-gray-100">
                 <tr>
                   <th className="p-4 text-sm font-bold text-gray-600">Nome</th>
                   <th className="p-4 text-sm font-bold text-gray-600">E-mail (Login)</th>
                   <th className="p-4 text-sm font-bold text-gray-600">Nível de Acesso</th>
                   <th className="p-4 text-sm font-bold text-gray-600 text-right">Ações</th>
                 </tr>
               </thead>
               <tbody className="divide-y divide-gray-100">
                 {users.map(user => (
                   <tr key={user.uid} className="hover:bg-gray-50 transition-colors">
                     <td className="p-4 font-medium text-gray-800">{user.displayName}</td>
                     <td className="p-4 text-gray-600">{user.email}</td>
                     <td className="p-4">{getRoleLabel(user.role)}</td>
                     <td className="p-4 text-right flex justify-end gap-3">
                        <button 
                          onClick={() => handleOpenEdit(user)}
                          className="text-blue-500 hover:text-blue-700"
                          title="Editar"
                        >
                          <Edit2 size={18} />
                        </button>
                        {user.uid !== authUser?.uid && (
                          <button 
                            onClick={() => handleDelete(user.uid)}
                            className="text-red-400 hover:text-red-600"
                            title="Remover Acesso"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
          </div>
          {users.length === 0 && (
            <div className="p-8 text-center text-gray-400">Nenhum usuário cadastrado.</div>
          )}
       </div>

       {showModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
             <div className="flex justify-between items-center mb-6">
               <h3 className="text-lg font-bold text-gray-800">
                 {editingUser.uid ? 'Editar Usuário' : 'Adicionar Acesso'}
               </h3>
               <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                 <X size={20} />
               </button>
             </div>
             
             <form onSubmit={handleSave} className="space-y-4">
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
                 <input 
                   type="text" required
                   className="w-full p-2 border rounded text-gray-900"
                   value={editingUser.displayName || ''}
                   onChange={e => setEditingUser({...editingUser, displayName: e.target.value})}
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">E-mail (Google Account)</label>
                 <input 
                   type="email" required
                   className="w-full p-2 border rounded text-gray-900"
                   placeholder="email@gmail.com"
                   value={editingUser.email || ''}
                   onChange={e => setEditingUser({...editingUser, email: e.target.value})}
                   disabled={!!editingUser.uid} // Cannot change email for identity reasons easily
                 />
                 {editingUser.uid && <span className="text-xs text-gray-400">Para alterar o e-mail, remova e crie novamente.</span>}
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Nível de Permissão</label>
                 <select 
                   className="w-full p-2 border rounded bg-white text-gray-900"
                   value={editingUser.role || 'staff'}
                   onChange={e => setEditingUser({...editingUser, role: e.target.value as Role})}
                 >
                   <option value="admin">Administrador (Acesso Total)</option>
                   <option value="manager">Gerente (Sem acesso a configurações/deletar)</option>
                   <option value="staff">Staff/Recepção (Operacional)</option>
                   <option value="guide">Guia de Pesca</option>
                   <option value="leader">Líder de Turma (Cliente)</option>
                 </select>
               </div>
               
               <div className="flex justify-end gap-3 mt-6">
                 <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded text-gray-600">Cancelar</button>
                 <button type="submit" className="px-4 py-2 bg-[#FF6B35] text-white rounded font-bold hover:bg-[#E85A2C]">
                   {isSaving ? 'Salvando...' : 'Confirmar'}
                 </button>
               </div>
             </form>
           </div>
         </div>
       )}
    </div>
  );
};

export default Clients;
